<template>
  <div class="game-filter">
    <div class="game-filter__bar">
      <div class="page-container">

        <div class="heading-box">
          <h3>Filter Games</h3>
          <div class="close_icon" @click="handleShowFilter(false)"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><path d="M310.6 361.4c12.5 12.5 12.5 32.75 0 45.25C304.4 412.9 296.2 416 288 416s-16.38-3.125-22.62-9.375L160 301.3L54.63 406.6C48.38 412.9 40.19 416 32 416S15.63 412.9 9.375 406.6c-12.5-12.5-12.5-32.75 0-45.25l105.4-105.4L9.375 150.6c-12.5-12.5-12.5-32.75 0-45.25s32.75-12.5 45.25 0L160 210.8l105.4-105.4c12.5-12.5 32.75-12.5 45.25 0s12.5 32.75 0 45.25l-105.4 105.4L310.6 361.4z"/></svg></div>
        </div>

        <!-- Filter: Regions -->
        <div
          v-if="includeRegions"
          class="game-filter__group clickable"
          :class="{
        'game-filter__group--active': activeDropdown.includes('filter-regions')
      }"
          @click.stop="activeDropdown.includes('filter-regions')? activeDropdown.splice(activeDropdown.indexOf('filter-regions')) : activeDropdown.push('filter-regions')"
        >

          <!-- Filter: Regions > Icon -->
          <game-filter-icon
            icon="filter-regions"
            title="Regions"
            :badge="filters.regions.length"
            :active="activeDropdown.includes('filter-regions')"
          />

          <!-- Filter: Regions > Dropdown -->
          <region-selector
            v-show="activeDropdown.includes('filter-regions')"
            class="game-filter__dropdown game-filter__dropdown--regions"
            v-model="filters.regions"
            :isMultiSelect="true"
            :hasSelectedSummary="true"
            @click.native.stop
            @input="filterChanged('regions')"
          >
            <template v-slot:footerAction>
              <span
                class="link link--action"
                @click="filters.regions = []; filterChanged('regions'); activeDropdown = null;"
              >View all regions</span>
            </template>
          </region-selector>
        </div>

        <!-- Filter: Game Type -->
        <div
          class="game-filter__group clickable"
          :class="{
        'game-filter__group--active': activeDropdown.includes('filter-game-type')
      }"
          @click.stop="activeDropdown.includes('filter-game-type')? activeDropdown.splice(activeDropdown.indexOf('filter-game-type')) : activeDropdown.push('filter-game-type')"
        >

          <!-- Filter: Game Type > Icon -->
          <game-filter-icon
            icon="filter-game-type"
            title="Game Type"
            :badge="filters.type.length"
            :active="activeDropdown.includes('filter-game-type')"
          />

          <!-- Filter: Game Type > Dropdown -->
          <div
            v-show="activeDropdown.includes('filter-game-type')"
            class="game-filter__dropdown"
            @click.stop
          >
            <checkbox-field
              v-for="option in filterOptions.type"
              :key="option.value"
              v-model="filters.type"
              @input="filterChanged(option)"
            >
              <option :value="option.value">{{ option.name }}</option>
            </checkbox-field>
          </div>
        </div>

        <!-- Filter: Game Theme -->
        <div
          class="game-filter__group clickable"
          :class="{
        'game-filter__group--active': activeDropdown.includes('filter-game-theme')
      }"
          @click.stop="activeDropdown.includes('filter-game-theme')? activeDropdown.splice(activeDropdown.indexOf('filter-game-theme')) : activeDropdown.push('filter-game-theme')"
        >

          <!-- Filter: Game Theme > Icon -->
          <game-filter-icon
            icon="filter-game-theme"
            title="Game Theme"
            :badge="filters.theme.length"
            :active="activeDropdown.includes('filter-game-theme')"
          />

          <!-- Filter: Game Theme > Dropdown -->
          <div
            v-show="activeDropdown.includes('filter-game-theme')"
            class="game-filter__dropdown"
            @click.stop
          >
            <text-field
              class="game-filter__dropdown__search search-custom"
              placeholder="Search for a game theme"
              v-model="themeSearch"
            />
            <checkbox-field
              v-for="option in filterOptionsTheme"
              :key="option.value"
              v-model="filters.theme"
              @input="filterChanged(option)"
            >
              <option :value="option.value">{{ option.name }}</option>
            </checkbox-field>
          </div>
        </div>

        <!-- Filter: Volatility -->
        <div
          class="game-filter__group clickable"
          :class="{
            'game-filter__group--active': activeDropdown.includes('filter-volatility')
          }"
          @click.stop="activeDropdown.includes('filter-volatility')? activeDropdown.splice(activeDropdown.indexOf('filter-volatility')) : activeDropdown.push('filter-volatility')"
        >

          <!-- Filter: Volatility > Icon -->
          <game-filter-icon
            icon="filter-volatility"
            title="Volatility"
            :badge="!!filters.volatility"
            :active="activeDropdown.includes('filter-volatility')"
          />

          <!-- Filter: Volatility > Dropdown -->
          <div
            v-show="activeDropdown.includes('filter-volatility')"
            class="game-filter__dropdown"
            @click.stop
          >
            <volatility-rating
              :read-only="false"
              :show-title="false"
              :volatility-slug="filters.volatility"
              @volatilityChanged="setVolatility"
            />
          </div>
        </div>

        <!-- Filter: Partners -->
        <div
          class="game-filter__group clickable"
          :class="{
            'game-filter__group--active': activeDropdown.includes('filter-partners')
          }"
          @click.stop="activeDropdown.includes('filter-partners')? activeDropdown.splice(activeDropdown.indexOf('filter-partners')) : activeDropdown.push('filter-partners')"
        >

          <!-- Filter: Partners > Icon -->
          <game-filter-icon
            icon="filter-partners"
            title="Partners"
            :badge="filters.partner.length"
            :active="activeDropdown.includes('filter-partners')"
          />

          <!-- Filter: Partners > Dropdown -->
          <div
            v-show="activeDropdown.includes('filter-partners')"
            class="game-filter__dropdown"
            @click.stop
          >
            <checkbox-field
              v-for="option in filterOptions.partner"
              :key="option.value"
              v-model="filters.partner"
              @input="filterChanged(option)"
            >
              <option :value="option.value">{{ option.name }}</option>
            </checkbox-field>
          </div>
        </div>

        <!-- Filter: More -->
        <div
          class="game-filter__group clickable"
          :class="{
            'game-filter__group--active': activeDropdown.includes('filter-more')
          }"
          @click.stop="activeDropdown.includes('filter-more')? activeDropdown.splice(activeDropdown.indexOf('filter-more')) : activeDropdown.push('filter-more')"
        >

          <!-- Filter: More > Icon -->
          <game-filter-icon
            icon="filter-more"
            title="More"
            :badge="null"
            :active="activeDropdown.includes('filter-more')"
          />

          <!-- Filter: More > Dropdown -->
          <div
            v-show="activeDropdown.includes('filter-more')"
            class="game-filter__dropdown more_dropdown"
            @click.stop
          >
						<label-field>Paylines</label-field>
            <div class="game-filter__dropdown__section game-filter__dropdown__section--grid">
              <radio-field
                v-model="filters.paylines"
                class="game-filter__paylines"
                @input="setPaylines"
              >
                <option value="1">0–25</option>
                <option value="2">26–50</option>
                <option value="3">51–100</option>
                <option value="4">101+</option>
              </radio-field>
            </div>
            <div class="game-filter__dropdown__section">
              <filter-slider
                class="game-filter__slider"
                label="Minimum Stake"
                :min="filtersDefault.minStake[0]"
                :max="filtersDefault.minStake[1]"
                :interval="0.01"
                v-model="filters.minStake"
                @input="filterChanged()"
              />
              <filter-slider
                class="game-filter__slider"
                label="Maximum Stake"
                :min="filtersDefault.maxStake[0]"
                :max="filtersDefault.maxStake[1]"
                :interval="10"
                v-model="filters.maxStake"
                @input="filterChanged()"
              />
            </div>
            <div class="game-filter__dropdown__section">
              <filter-slider
                class="game-filter__slider"
                label="RTP"
                :min="filtersDefault.rtp[0]"
                :max="filtersDefault.rtp[1]"
                :interval="0.01"
                v-model="filters.rtp"
                @input="filterChanged()"
                @click.stop
              />
            </div>
          </div>
        </div>

        <!-- Filter: Search mobile -->
        <div
          class="game-filter__group clickable hide-md visible-xs"
          :class="{
            'game-filter__group--active': activeDropdown.includes('filter-search')
          }"
          @click.stop="activeDropdown.includes('filter-search')? activeDropdown.splice(activeDropdown.indexOf('filter-search')) : activeDropdown.push('filter-search')"
        >

          <!-- Filter: Search > Icon -->
          <game-filter-icon
            icon="filter-search"
            title="Search"
            :badge="filters.partner.length"
            :active="activeDropdown.includes('filter-search')"
          />

          <!-- Filter: Search -->
          <div
            class="game-filter__search mobile-search-box"
            v-show="activeDropdown.includes('filter-search')"
            @click.stop
          >
            <autocomplete
              ref="search"
              placeholder="Search for a game..."
              :items="autocompleteData"
              @input="gameSearched"
            />
          </div>
        </div>

        <!-- Filter: Search -->
        <div class="game-filter__search hide-xs">
          <autocomplete
            ref="search"
            placeholder="Search for a game..."
            :items="autocompleteData"
            @input="gameSearched"
          />
        </div>

      </div>
    </div>
    <div class="page-container">
      <div class="game-filter__selected-filters">
        <div class="active-filters">
          <div
            v-for="filter in activeFilters"
            :key="filter.value"
            class="active-filters__filter clickable link--close"
            @click="removeFilter(filter)"
          >
            <span v-if="filter.type !== 'regions'">{{ filter.type }}:</span>
            <span>{{ filter.name }}</span>
          </div>
        </div>
        <span class="active-filters__reset link--close clickable" @click="this.reset">
          Reset Filters
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { cloneDeep } from 'lodash'

import Icon from '../atoms/GameFilterIcon'
import TextField from '../atoms/TextField'
import CheckboxField from '../atoms/CheckboxField'
import RegionSelector from '../atoms/RegionSelector'
import VolatilityRating from '../molecules/VolatilityRating'
import Autocomplete from '../molecules/Autocomplete'
import GameFilterIcon from '../atoms/GameFilterIcon'
import FilterSlider from '../atoms/FilterSlider'
import RadioField from '../atoms/RadioField'
import LabelField from '../atoms/LabelField'

export default {
  name: 'GameFilter',
  components: {
    RadioField,
    GameFilterIcon,
    Icon,
    TextField,
    CheckboxField,
    RegionSelector,
    VolatilityRating,
    Autocomplete,
    FilterSlider,
		LabelField
  },
  data () {
    return {
      showRegionSelector: false,
      activeDropdown: [],
      showSearch: false,
      themeSearch: '',
      filtersDefault: {
        minStake: [0, 1],
        maxStake: [0.00, 30000],
        partners: [],
        paylines: '',
        regions: [this.$root.userSelectedRegion.key],
        rtp: [80, 99.99],
        search: null,
        sort: [
          { 'regionalReleaseDate': 'desc' }
        ],
        type: [],
        theme: [],
        volatility: null,
        partner: []
      },
      filters: null
    }
  },
  props: {
    isVertical: {
      type: Boolean,
      default: true
    },
    centralise: {
      type: Boolean,
      default: true
    },
    includeRegions: {
      type: Boolean,
      default: true
    },
    autocompleteData: Array,
  },
  computed: {
    ...mapGetters({
      getRegionLabel: 'app/regionLabel'
    }),
    filterOptions () {
      const games = cloneDeep(this.$root.$data.games)

      // Define filter option to generate and prepare arrays
      const filterOptions = {
        type: [],
        theme: [],
        partner: []
      }

      // Generate filter options for each defined key
      Object.keys(filterOptions).forEach(key => {

        // Loop through each game and selected option key
        Object.entries(games).forEach(game => {
          if (!game[1][key]) return
          if (typeof game[1][key] !== 'object') return
          game[1][key].forEach(option => {

            // Don't include null or "none" values
            if (!option.value || !option.name) return
            if (option.value === key + '-none') return

            // Check if filter option already exists
            if (filterOptions[key].findIndex(o => o.value === option.value) === -1) {
              filterOptions[key].push(option)
            }
          })
        })

        // Sort filter options
        filterOptions[key].sort((a, b) => a.name.localeCompare(b.name))
      })
      return filterOptions
    },
    filterOptionsTheme () {
      return this.filterOptions.theme.filter(o => o.name.toLowerCase().includes(this.themeSearch.toLowerCase()))
    },
    activeFilters () {

      // Check filters is not null
      if (!this.filters) return

      // Define new array for active filters
      const activeFilters = []

      // Special case for regions
      if (Object.hasOwn(this.filters, 'regions')) {
        this.filters.regions.forEach(region => {

          // Find the associated filter option
          activeFilters.push({
            type: 'regions',
            name: this.getRegionLabel(region),
            value: region
          })
        })
      }

      // Check each defined filter option
      Object.keys(this.filterOptions).forEach(key => {
        if (Object.hasOwn(this.filters, key)) {

          this.filters[key].forEach(filter => {

            // Find the associated filter option
            const selectedFilter = this.filterOptions[key].find(o => o.value === filter)
            if (selectedFilter) {

              // Augment selected filter with filter type
              selectedFilter.type = key
              activeFilters.push(selectedFilter)
            }
          })

        }
      })

      // Special case for volatility
      if (Object.hasOwn(this.filters, 'volatility') && this.filters.volatility) {
        activeFilters.push({
          type: 'volatility',
          name: this.filters.volatility,
          value: this.filters.volatility
        })
      }
      return activeFilters
    }
  },
  watch: {
    '$root.userSelectedRegionKey': function () {
      this.filtersDefault.regions = [this.$root.userSelectedRegion.key]
      this.filters.regions = [this.$root.userSelectedRegion.key]
      this.filterChanged('regions')
    },
  },
  created: function () {
    this.$eventBus.$on('window-click', this.closeAllPopups)
    this.filters = cloneDeep(this.filtersDefault)
  },
  beforeDestroy: function () {
    this.$eventBus.$off('window-click', this.closeAllPopups)
  },
  methods: {
    closeAllPopups () {
      this.activeDropdown = []
      this.showSearch = false
    },
		closeFilters (closed) {
			activeDropdown.includes(closed) ?
					activeDropdown.filter(item => item !== closed) :
					activeDropdown.push(closed)
		},
    removeFilter (filter) {
      if (filter.type === 'volatility') {
        this.filters.volatility = null
      } else {
        this.filters[filter.type] = this.filters[filter.type].filter(o => o !== filter.value)
      }
      this.filterChanged(filter.type)
    },
    filterChanged (name) {
      var self = this
      self.$emit('filterChanged', self.filters)
      if (name) {
        self.trackEvent([
          'filter-changed',
          name,
          self.$root.$data.user ? self.$root.$data.user.trackName : null,
          0,
          false
        ])
      }
    },
    handleShowFilter (showFilter) {
      var self = this;
      self.$emit('handleShowFilter', showFilter)
    },
    setVolatility (volatility) {
      var self = this
      self.filters.volatility = self.filters.volatility !== volatility ? volatility : null
      self.$emit('filterChanged', self.filters)
      self.trackEvent([
        'filter-changed',
        'volatility',
        self.$root.$data.user ? self.$root.$data.user.trackName : null,
        0,
        false
      ])
    },
    setPaylines (paylines) {
      const self = this
      this.filters.paylines = paylines !== null && this.filters.paylines !== paylines ? paylines : ''
      self.filters.paylines = paylines !== null && self.filters.paylines !== paylines ? paylines : ''
      self.$emit('filterChanged', self.filters)
    },
    gameSearched (gameId) {
      var self = this
      self.filters.search = gameId
      self.$emit('filterChanged', self.filters)

      // If not resetting...
      if (gameId !== null) {
        self.trackEvent([
          'filter-changed',
          'game-search',
          self.$root.$data.user ? self.$root.$data.user.trackName : null,
          0,
          false
        ])
      }
    },
    reset () {
      var self = this
      self.filters = cloneDeep(self.filtersDefault)
      self.filters.regions = [this.$root.userSelectedRegion.key]
      self.$refs.search.setResult(self.filters.search)
      self.$emit('filterChanged', self.filters)
    }
  }
}
</script>

<style lang="scss">
@import "src/scss/toolkit";

.game-filter {
  padding: 0;

  &__bar {
    border-bottom: 1px solid palette(lighter-grey);

    .page-container {
      display: flex;

			@include media(phab) {
				margin-top: 0.5rem;
				flex-flow: wrap;
			}
    }
  }

  &__selected-filters {
    display: flex;
    justify-content: space-between;
    padding: 2rem 0 1.5rem;
    border-bottom: 1px solid palette(lighter-grey);

    .active-filters {
      display: flex;
      flex-wrap: wrap;
    }

    .active-filters__reset {
      font-size: 0.75rem;
      padding: .875rem 0 1.3375rem;
      font-weight: bold;
      text-transform: uppercase;
      color: palette(blue);
      white-space: nowrap;
    }

    .active-filters__filter {
      font-size: 0.625rem;
      padding: .875rem .75rem;
      font-weight: bold;
      text-transform: uppercase;
      color: palette(grey);
      background-color: palette(white);
      border: 1px solid palette(divider);
      border-radius: 3px;
      margin: 0 0.5rem 0.5rem 0;

      &:hover {
        background-color: palette(lightest-grey);
      }
    }
  }

  &__group {
    position: relative;

		@include media(phab) {
			flex: 0 0 50%;

			&:nth-child(odd) .game-filter__icon {
				margin-right: 0.25rem;
			}

			&:nth-child(even) .game-filter__icon {
				margin-left: 0.25rem;
			}
		}

		@include media(not-phab) {
			margin-left: -1px;
			border: solid palette(lighter-grey);
			border-width: 0 1px;

			&:first-child {
				border-left-color: transparent;
			}

			&:nth-last-child(2) {
				border-right-color: transparent;
			}

			&--active {

				&:first-child,
				&:nth-last-child(2) {
					border-color: palette(lighter-grey);
				}

				.game-filter__icon {
					z-index: 13;
				}
			}
		}
  }

  &__dropdown {
    position: absolute;
    top: calc(100% - 0.5rem);
    left: calc(50% - 10rem);
    width: 20rem;
		max-height: 50vh;
		overflow-y: scroll;
    padding: 1.5rem 2rem;
    border: 1px solid palette(lighter-grey);
    background: palette(white);
    box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.05);
    z-index: 12;

    &:hover {
      cursor: default;
    }

    &__section {

      & + .game-filter__dropdown__section {
        margin-top: 1.25rem;
        border-top: 1px solid palette('lighter-grey');
        padding-top: 1.25rem;
      }
    }

    &--regions {
      width: 30rem;
      left: -1px;
    }

    &__search {
      margin-bottom: 0.75rem;

      input {
        box-shadow: inset 0 1px 3px 0 rgba(0, 0, 0, 0.15);
      }
    }

    .field--checkbox {
      padding: 0.5rem 0;
      border-bottom: 1px solid palette(lighter-grey);

      &:last-child {
        border-bottom-width: 0;
      }

      .field__sub-label:before {
        margin-right: 0.75rem;
        width: 1.125rem;
        height: 1.125rem;
        font-size: 0.5rem;
      }

      .field__element:checked + .field__sub-label {

        &:before {
          color: palette(blue);
          border: 2px solid palette(blue);
        }

        span {
          color: palette(blue);
        }
      }
    }
  }

  &__region-selector {

    .game-filter--horizontal & {
      position: absolute;
      left: 0;
      top: calc(100% - 8px);
      box-shadow: inset 0 0 0 1px palette(lighter-grey), 0 4px 8px rgba(0, 0, 0, 0.05);
      z-index: 99;
    }

    .game-filter--vertical & {
      max-width: none;
      width: 100%;
      padding: 0;
    }
  }

  &__search {
    display: flex;
    justify-content: end;
    align-items: center;
    flex-grow: 1;
    color: palette(grey);
    background-color: palette(white);

		@include media(phab) {
			padding: 1rem 0;
		}

		@include media(not-phab) {
			padding-left: 2rem;
		}

		& > * {
			width: 100%;

			@include media(not-phab) {
				max-width: 20rem;
			}
		}
  }

  &__slider + .game-filter__slider {
    margin-top: rem(20);
  }

  &__paylines {

    .field__element-group {
      display: grid;
      grid-gap: 0.25rem;
      grid-template-columns: repeat(auto-fit, minmax(calc(25% - 0.75rem), 1fr));
    }

    .field__element-wrap {

      .field__sub-label {
        padding: rem(12) 0;
        border: 1px solid palette('divider');
        border-radius: 3px;
        font-size: rem(10);
        font-weight: bold;
        text-align: center;
        text-indent: 0;

        span {
          color: palette('grey');
        }

        &::before {
          display: none;
        }
      }

      .field__element:checked + .field__sub-label {
        border-color: transparent;
        background: palette('blue');

        span {
          color: palette('white');
        }
      }
    }
  }

	.label {
		display: block;
		margin-bottom: rem(8);
		font-size: rem(14);
		color: palette('grey');
	}

  .volatility-rating {
    width: 100%;
    display: block;
    padding: 1rem 1rem 0.5rem;
    min-height: 3.75rem;

    &__container {
      width: 100%;
    }

    &__name {
      margin: 0.5rem -0.375rem 0;
    }
  }
}
.heading-box{
  display: none;
}
.hide-md{
  display: none!important;
}

@media(max-width:767px){
  .games__filter-wrap{
    position: fixed!important;
    overflow: auto;
       background: #fff;
    top: 0;
    width: 90%;
    right: 0;
    z-index: 1111;
    height: 100%;
  }
  .game-filter__group{
        flex: 0 0 100%;
  }
  .game-filter__group:nth-child(even) .game-filter__icon{
    margin-left: 0!important;
  }
  .game-filter__group:nth-child(odd) .game-filter__icon{
    margin-right: 0!important;
  }
  .game-filter__bar .page-container{
    padding: 0!important;
    margin-top: 0!important;
    margin-bottom: 70px;
  }
  .game-filter__icon{
    border: none!important;
    border-bottom: 1px solid #DDDDDD!important;
    padding: 20px!important;
  }
  .game-filter__icon__badge{
    background-color: #0179FF!important;
    top: 20px!important;
    position: absolute;
    right: 55px!important;
    height: 25px;
    line-height: 25px!important;
    width: 25px;
  }
  .game-filter__dropdown{
    width: 100%!important;
		max-width:100%!important;
    padding: 0!important;
    left: 0;
    position: relative!important;
    top: 0!important;
    overflow: hidden!important;
    height: auto!important;
    max-height: initial!important;
    box-shadow: none!important;
    border: none!important;
    border-bottom: 1px solid #dddddd!important;
  }
  .region-selector__group{
    padding: 14px!important;
    border-bottom: 1px solid #DDDDDD;
  }
  .region-selector__group-body{
    padding-bottom: 0!important;
        padding-top: 1rem!important;
  }
  .region-selector hr{
    display: none;
  }
  .region-selector__group-header{
        padding: 0 0 0 0.5rem!important;
  }
  .region-selector__footer{
    padding-bottom: 1.5rem;
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }
  .heading-box{
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 15px 20px;
    background: #eee;

  }
  .heading-box h3{
    font-size: 20px;
    font-weight: 600;
    margin-bottom: 0!important;
  }
  .close_icon svg{
        width: 20px;
    height: 20px;
    fill: #000;
  }
  .game-filter__selected-filters{
    display: none;
  }
  .games__filter-close button{
    color: #0179FF!important;
    background-color: transparent!important;
  }
  .game-filter__dropdown .field--checkbox{
        padding: 15px 20px 7px;
  }
  .search-custom .field__element-wrap{
    padding: 10px 20px 0px;
  }
  .more_dropdown{
    padding: 20px!important;
  }
  .hide-xs{
    display: none;
  }
  .visible-xs{
    display: block!important;
  }
  .mobile-search-box{
    margin: 0px 20px;
  }
	.game-filter__dropdown .field--checkbox .field__sub-label:before{
		border-radius: 2px!important;
	}
	.games__filter-close{
		position: fixed;
    bottom: 0;
    background: #fff;
    margin: auto;
    width: 100%;
    padding: 20px 0px;
    z-index: 111111;
	}

}

</style>



// WEBPACK FOOTER //
// src/components/organisms/GameFilter.vue